import { request } from '@/lib/api'

/**
 * Обёртки над `/api/v1/admin` (тикет #97).
 *
 * Каждая вызывается экраном — обёртка без вызывающего выглядит готовой возможностью,
 * которой в интерфейсе нет (урок #49 и #94).
 */

export interface Currency {
  code: string
  numericCode: string
  name: string
  symbol: string | null
  minorUnits: number
}

export interface Unit {
  id: string
  code: string
  okeiCode: string | null
  name: string
  kind: string
  precisionDigits: number
}

export type ResetPeriod = 'yearly' | 'monthly' | 'never'

export interface NumberSequence {
  id: string
  documentType: string
  scopeKey: string
  mask: string
  resetPeriod: ResetPeriod
  currentValue: number
  nextExample: string
}

export interface Threshold {
  id: string
  name: string
  entityType: string
  organizationId: string | null
  minDiscountPercent: number | null
  minAmount: number | null
  currency: string | null
  approverUserId: string | null
  approverRoleId: string | null
  stepOrder: number
  escalateAfterHours: number | null
  active: boolean
}

export type ThresholdInput = Omit<Threshold, 'id' | 'entityType'> & { entityType?: string }

export interface Settings {
  version: number
  timeZone: string
  workDayStart: string
  workDayEnd: string
  workDays: number[]
  defaultOrganizationId: string | null
  /** За сколько минут напоминать о задаче или встрече, если форма не указала своё (#111). */
  defaultReminderMinutes?: number | null
  updatedAt: string | null
}

export interface Organization {
  id: string
  name: string
  inn: string
}

export interface Approvers {
  roles: { id: string; code: string; name: string }[]
  users: { id: string; username: string; fullName: string }[]
}

export const fetchCurrencies = (signal?: AbortSignal) =>
  request<{ currencies: Currency[] }>('/admin/currencies', { signal }).then((r) => r.currencies)

export const createCurrency = (body: Currency) =>
  request<Currency>('/admin/currencies', { method: 'POST', body })

export const updateCurrency = (body: Currency) =>
  request<Currency>(`/admin/currencies/${encodeURIComponent(body.code)}`, { method: 'PUT', body })

export const fetchUnits = (signal?: AbortSignal) =>
  request<{ units: Unit[]; kinds: string[] }>('/admin/units', { signal })

export const createUnit = (body: Omit<Unit, 'id'>) =>
  request<Unit>('/admin/units', { method: 'POST', body })

export const updateUnit = (body: Unit) =>
  request<Unit>(`/admin/units/${body.id}`, { method: 'PUT', body })

/** Причина закрытия сделки (#100). */
export interface AdminDealReason {
  id: string
  kind: 'lost' | 'won'
  code: string
  name: string
  sortOrder: number
  active: boolean
}

export const fetchDealReasons = (signal?: AbortSignal) =>
  request<{ reasons: AdminDealReason[] }>('/admin/deal-reasons', { signal }).then((r) => r.reasons)

export const createDealReason = (body: Omit<AdminDealReason, 'id'>) =>
  request<AdminDealReason>('/admin/deal-reasons', { method: 'POST', body })

export const updateDealReason = (id: string, body: Omit<AdminDealReason, 'id'>) =>
  request<AdminDealReason>(`/admin/deal-reasons/${id}`, { method: 'PUT', body })

export const fetchSequences = (signal?: AbortSignal) =>
  request<{ sequences: NumberSequence[] }>('/admin/number-sequences', { signal }).then(
    (r) => r.sequences,
  )

export const updateSequence = (id: string, mask: string, resetPeriod: ResetPeriod) =>
  request<NumberSequence>(`/admin/number-sequences/${id}`, {
    method: 'PUT',
    body: { mask, resetPeriod },
  })

export const previewSequence = (mask: string, resetPeriod: ResetPeriod, currentValue: number) =>
  request<{ example: string }>('/admin/number-sequences/preview', {
    method: 'POST',
    body: { mask, resetPeriod, currentValue },
  }).then((r) => r.example)

export const fetchThresholds = (signal?: AbortSignal) =>
  request<{ thresholds: Threshold[]; documentTypes: string[] }>('/admin/approval-thresholds', {
    signal,
  })

export const createThreshold = (body: ThresholdInput) =>
  request<Threshold>('/admin/approval-thresholds', { method: 'POST', body })

export const updateThreshold = (id: string, body: ThresholdInput) =>
  request<Threshold>(`/admin/approval-thresholds/${id}`, { method: 'PUT', body })

export const fetchSettings = (signal?: AbortSignal) =>
  request<Settings>('/admin/settings', { signal })

export const updateSettings = (body: Omit<Settings, 'updatedAt'>) =>
  request<Settings>('/admin/settings', { method: 'PUT', body })

export const fetchOrganizations = (signal?: AbortSignal) =>
  request<{ organizations: Organization[] }>('/admin/organizations', { signal }).then(
    (r) => r.organizations,
  )

export const fetchApprovers = (signal?: AbortSignal) =>
  request<Approvers>('/admin/approvers', { signal })

// ------------------------------------------------------------------ доступ (#102)

export interface AccessAssignment {
  roleId: string
  roleCode: string
  roleName: string
  expiresAt: string | null
  expired: boolean
}

export interface AccessUser {
  id: string
  username: string
  fullName: string
  email: string | null
  phone?: string | null
  /**
   * Последний запрос с действующим токеном, с точностью до интервала отметки (5 минут), а не
   * момент ввода пароля: вход видит Keycloak, приложение видит запросы. Нет поля — с этой
   * учётной записью не приходили с тех пор, как отметка ведётся.
   */
  lastLoginAt?: string | null
  active: boolean
  employeeId: string | null
  employeeName: string | null
  keycloakLinked: boolean
  external: boolean
  roles: AccessAssignment[]
}

export interface AccessRole {
  id: string
  code: string
  name: string
  description: string | null
  parentId: string | null
  parentCode: string | null
  system: boolean
  holders: number
}

/** Строка матрицы: `scope` — прямая выдача этой роли, `inheritedScope` — от предков. */
export interface MatrixRow {
  permissionId: string
  resource: string
  action: string
  field: string | null
  scope: Scope | null
  inheritedScope: Scope | null
}

export type Scope = 'own' | 'department' | 'all'

export const fetchAccessUsers = (query: string, signal?: AbortSignal) =>
  request<{ users: AccessUser[] }>(`/admin/users${query ? `?query=${encodeURIComponent(query)}` : ''}`, {
    signal,
  }).then((r) => r.users)

export const createAccessUser = (body: {
  username: string
  fullName: string
  email: string | null
  phone?: string | null
  employeeId: string | null
}) =>
  request<AccessUser>('/admin/users', { method: 'POST', body })

export const updateAccessUser = (id: string, body: Record<string, unknown>) =>
  request<AccessUser>(`/admin/users/${id}`, { method: 'PUT', body })

export const assignAccessRole = (userId: string, roleId: string, expiresAt: string | null) =>
  request<AccessUser>(`/admin/users/${userId}/roles`, { method: 'POST', body: { roleId, expiresAt } })

export const revokeAccessRole = (userId: string, roleId: string) =>
  request<AccessUser>(`/admin/users/${userId}/roles/${roleId}`, { method: 'DELETE' })

export const fetchAccessRoles = (signal?: AbortSignal) =>
  request<{ roles: AccessRole[] }>('/admin/roles', { signal }).then((r) => r.roles)

export const createAccessRole = (body: { code: string; name: string; parentId: string | null }) =>
  request<AccessRole>('/admin/roles', { method: 'POST', body })

/** Встроенную роль сервер переименовать не даст; описание — можно. */
export const updateAccessRole = (id: string, body: { name?: string; description?: string }) =>
  request<AccessRole>(`/admin/roles/${id}`, { method: 'PUT', body })

export const deleteAccessRole = (id: string) => request<void>(`/admin/roles/${id}`, { method: 'DELETE' })

export const fetchRoleMatrix = (id: string, signal?: AbortSignal) =>
  request<{ permissions: MatrixRow[] }>(`/admin/roles/${id}/permissions`, { signal }).then((r) => r.permissions)

/** Матрица уходит целиком: не названное право снимается. */
export const saveRoleMatrix = (id: string, grants: { permissionId: string; scope: Scope }[]) =>
  request<{ permissions: MatrixRow[] }>(`/admin/roles/${id}/permissions`, { method: 'PUT', body: { grants } }).then(
    (r) => r.permissions,
  )

// ------------------------------------------------------------------ воронки и распределение (#103)

export interface AdminStage {
  id: string
  code: string
  name: string
  stageOrder: number
  probability: number
  stageType: 'open' | 'won' | 'lost'
  maxDaysInStage: number | null
  color: string | null
  openDeals: number
}

export interface AdminFunnel {
  id: string
  code: string
  name: string
  description: string | null
  isDefault: boolean
  active: boolean
  stages: AdminStage[]
}

export interface StageBody {
  code?: string
  name?: string
  position?: number
  probability?: number
  maxDaysInStage?: number
  color?: string
}

export const fetchAdminFunnels = (signal?: AbortSignal) =>
  request<{ funnels: AdminFunnel[] }>('/admin/funnels', { signal }).then((r) => r.funnels)

export const createAdminFunnel = (body: { code: string; name: string; description?: string }) =>
  request<AdminFunnel>('/admin/funnels', { method: 'POST', body })

export const updateAdminFunnel = (
  id: string,
  body: { name?: string; description?: string; active?: boolean; isDefault?: boolean },
) => request<AdminFunnel>(`/admin/funnels/${id}`, { method: 'PUT', body })

export const addFunnelStage = (funnelId: string, body: StageBody) =>
  request<AdminFunnel>(`/admin/funnels/${funnelId}/stages`, { method: 'POST', body })

export const updateFunnelStage = (funnelId: string, stageId: string, body: StageBody) =>
  request<AdminFunnel>(`/admin/funnels/${funnelId}/stages/${stageId}`, { method: 'PUT', body })

export const reorderFunnelStages = (funnelId: string, stageIds: string[]) =>
  request<AdminFunnel>(`/admin/funnels/${funnelId}/stage-order`, { method: 'PUT', body: { stageIds } })

export const removeFunnelStage = (funnelId: string, stageId: string) =>
  request<AdminFunnel>(`/admin/funnels/${funnelId}/stages/${stageId}`, { method: 'DELETE' })

export type RuleStrategy = 'round_robin' | 'by_load' | 'by_region' | 'manual'

export interface AdminLeadRule {
  id: string
  name: string
  strategy: RuleStrategy
  /** JSON-строка плоской карты условий, как она лежит в базе. */
  conditions: string
  departmentId: string | null
  departmentName: string | null
  employees: { id: string; name: string }[]
  priority: number
  active: boolean
}

export interface LeadRuleBody {
  name?: string
  strategy?: RuleStrategy
  conditions?: Record<string, string>
  departmentId?: string | null
  employeeIds?: string[]
  priority?: number
  active?: boolean
}

export const fetchLeadRules = (signal?: AbortSignal) =>
  request<{ rules: AdminLeadRule[] }>('/admin/lead-rules', { signal }).then((r) => r.rules)

export const createLeadRule = (body: LeadRuleBody) =>
  request<AdminLeadRule>('/admin/lead-rules', { method: 'POST', body })

export const updateLeadRule = (id: string, body: LeadRuleBody) =>
  request<AdminLeadRule>(`/admin/lead-rules/${id}`, { method: 'PUT', body })

export const deleteLeadRule = (id: string) => request<void>(`/admin/lead-rules/${id}`, { method: 'DELETE' })

// ------------------------------------------------------------------ теги и сегменты (#105)

/** Тег заводится и здесь, а не только из списка: общий набор задаёт администратор. */
export const createTag = (body: { name: string; color: string | null }) =>
  request<{ id: string; name: string; color?: string | null }>('/tags', { method: 'POST', body })

/** Общий или личный — решение администратора о том, кто видит сегмент (#105). */
export const updateSegmentSharing = (id: string, shared: boolean) =>
  request<unknown>(`/segments/${id}`, { method: 'PUT', body: { shared } })

// ------------------------------------------------------------------ налоговые ставки

/** Вид ставки — задаёт, какие признаки чека к ней подходят. */
export type TaxRateKind = 'standard' | 'reduced' | 'special' | 'zero' | 'exempt'

/**
 * Период ставки налога (V57).
 *
 * Один код — несколько периодов: `vat_standard` — 20 % до 31.12.2025 и 22 % с 01.01.2026.
 * `documentLines` и `products` приходят с сервера: по ним экран решает, что ещё можно
 * править, но запрет держит сервер (`admin.tax_rate.in_use`), а не экран.
 */
export interface TaxRate {
  id: string
  version: number
  code: string
  name: string
  kind: TaxRateKind
  percent: number
  estimated: boolean
  ofdCode: string
  validFrom: string
  validTo?: string | null
  documentLines: number
  products: number
}

/** Признак ставки в чеке (ФФД, тег 1199): перечень фиксирован в коде, выбирается, а не вводится. */
export interface OfdCode {
  code: string
  percent: number
  estimated: boolean
}

export interface TaxRateInput {
  code: string
  name: string
  kind: TaxRateKind
  percent: number
  estimated: boolean
  ofdCode: string
  validFrom: string
  validTo: string | null
}

export const fetchTaxRates = (signal?: AbortSignal) =>
  request<{ rates: TaxRate[]; kinds: TaxRateKind[]; ofdCodes: OfdCode[] }>('/admin/tax-rates', { signal })

export const createTaxRate = (body: TaxRateInput) =>
  request<TaxRate>('/admin/tax-rates', { method: 'POST', body })

/** `validTo` передаётся всегда: его отсутствие сервер читает как «без даты окончания». */
export const updateTaxRate = (id: string, body: TaxRateInput) =>
  request<TaxRate>(`/admin/tax-rates/${id}`, { method: 'PUT', body })

export const deleteTaxRate = (id: string) => request<void>(`/admin/tax-rates/${id}`, { method: 'DELETE' })
